import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    AgentRefereeRevenueReport,
    Mutation,
    Query,
    QueryGetAgentRevenueShareWeeklyReportV2Args,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {AgentLevelReportV2RequestBuilder} from './requestBuilder';
import type {AgentLevelReportV2DownloadFields, AgentLevelReportV2QueryKeys, AgentLevelReportV2ServerFilterKeys} from './types';

@injectable()
export class AgentLevelReportV2Service<TCodes extends BaseHttpCodes | string>
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetAgentRevenueShareWeeklyReportV2Args,
        AgentLevelReportV2QueryKeys,
        Mutation,
        Subscription,
        TCodes,
        AgentLevelReportV2ServerFilterKeys
    >
    implements IDownloadByLinkService<AgentLevelReportV2DownloadFields>
{
    protected readonly _reportRequestBuilder: AgentLevelReportV2RequestBuilder;
    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer<TCodes>
    ) {
        const builder = new AgentLevelReportV2RequestBuilder();
        super(client, builder, messageLocalizer);
        this._reportRequestBuilder = builder;
    }

    protected getErrorSeverity(_code: TCodes): number {
        return 0;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, AgentLevelReportV2QueryKeys>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, AgentRefereeRevenueReport>> {
        return this.fetchData(requestPayload).pipe(
            map(({status, message, errors, responsePayload}) => {
                const responseQuery = responsePayload?.getAgentRevenueShareWeeklyReportV2
                    ? responsePayload?.getAgentRevenueShareWeeklyReportV2
                    : responsePayload?.getAgentRevenueShareDailyReport;
                const items = responseQuery?.items ?? [];
                const total = responseQuery?.total_count ? responseQuery?.total_count : items.length;

                return {
                    status,
                    message,
                    errors,
                    requestPayload,
                    responsePayload: {
                        items,
                        total,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<AgentLevelReportV2DownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<AgentLevelReportV2DownloadFields>, string>> {
        const request = this._reportRequestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => {
                const responseQuery = response?.responsePayload?.exportAgentRevenueShareWeeklyReportV2
                    ? response?.responsePayload?.exportAgentRevenueShareWeeklyReportV2
                    : response?.responsePayload?.exportAgentRevenueShareDailyReport;
                return {
                    ...response,
                    requestPayload,
                    responsePayload: responseQuery?.url,
                };
            })
        );
    }
}
